// Core
import React from 'react';

// Layout
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../../layout/elements/Paragraph';
import { StatusIndicator, StatusIndicatorStatus } from '../../layout/elements/StatusIndicator';
import { Circle, ArrowRight } from '../../layout/icons';

// Component
import {
  PLCCardItem,
  PLCCardProps,
  PLCCardType,
} from './PLCCard.types';
import {
  AnalogItemContainer,
  Container,
  IconsContainer,
  ItemContainer,
  ItemContent,
  ItemsContainer,
  ItemValue,
  TextContainer,
  ValueContainer,
  Wrapper,
} from './PLCCard.style';

function PLCCard({
  className,
  items,
  type,
  testId,
  text,
}: PLCCardProps): JSX.Element {
  return (
    <Wrapper>
      <Container
        className={className}
        data-testid={testId}
        type={type}
      >
        <IconsContainer type={type}>
          <ArrowRight />
          <Circle />
        </IconsContainer>
        <TextContainer>
          <Paragraph
            color="white"
            size={ParagraphSize.sm}
            weight={ParagraphWeight.medium}
          >
            {text}
          </Paragraph>
        </TextContainer>
      </Container>
      <ItemsContainer>
        {type === PLCCardType.analog ? (
          // Logic for Analog: items with value have gray background connected
          items?.map((item: PLCCardItem, index: number) => {
            const prevItem: PLCCardItem | null = index > 0 ? items[index - 1] : null;

            // Check if this item should have gray background:
            // 1. If it has value (item with separator)
            // 2. If the previous item has value (item after separator)
            const hasValue: boolean = Boolean(item.value);
            const prevItemHasValue: boolean = Boolean(prevItem?.value);
            const shouldHaveGrayBackground: boolean = hasValue || prevItemHasValue;

            const itemKey: string | number = item.text || item.number || item.value || 'empty-item';
            const uniqueKey: string = `${testId}-analog-${itemKey}-${
              item.value ? 'with-value' : 'no-value'
            }-${index}`;

            return (
              <AnalogItemContainer
                key={uniqueKey}
                hasGrayBackground={shouldHaveGrayBackground}
                lastItem={Boolean(index === items.length - 1)}
              >
                <ItemContent>
                  {item.showStatusIndicator && item.status && (
                    <StatusIndicator status={item.status} />
                  )}
                  {item.number && (
                    <Paragraph
                      size={ParagraphSize.sm}
                      weight={ParagraphWeight.bold}
                    >
                      {item.number}
                    </Paragraph>
                  )}
                  {item.text && (
                    <Paragraph
                      size={ParagraphSize.sm}
                      weight={ParagraphWeight.regular}
                    >
                      {item.text}
                    </Paragraph>
                  )}
                </ItemContent>
                {item.value && (
                  <ValueContainer>
                    <ItemValue>
                      <Paragraph size={ParagraphSize.sm} weight={ParagraphWeight.regular}>
                        {item.value}
                      </Paragraph>
                    </ItemValue>
                  </ValueContainer>
                )}
              </AnalogItemContainer>
            );
          })
        ) : (
        // Logic for Input/Output: always 16 items (00-15)
          Array.from({ length: 16 }, (_, index: number): JSX.Element => {
            const item: PLCCardItem | undefined = items?.find(
              (i: PLCCardItem) => i.number === index.toString().padStart(2, '0'),
            ) || items?.[index];

            const displayNumber: string = index.toString().padStart(2, '0');

            return (
              <ItemContainer
                key={`${testId}-item-${displayNumber}`}
                hasGrayBackground={false}
                lastItem={index === 15}
              >
                <ItemContent>
                  <StatusIndicator status={item?.status || StatusIndicatorStatus.neutral} />
                  <Paragraph
                    size={ParagraphSize.sm}
                    weight={ParagraphWeight.bold}
                  >
                    {displayNumber}
                  </Paragraph>
                  {item?.text && (
                    <Paragraph
                      size={ParagraphSize.sm}
                      weight={ParagraphWeight.regular}
                    >
                      {item.text}
                    </Paragraph>
                  )}
                </ItemContent>
                {item?.value && (
                  <ItemValue>
                    <Paragraph size={ParagraphSize.sm} weight={ParagraphWeight.regular}>
                      {item.value}
                    </Paragraph>
                  </ItemValue>
                )}
              </ItemContainer>
            );
          })
        )}
      </ItemsContainer>
    </Wrapper>
  );
}
export { PLCCard };
